import { FC } from 'react';
import { X } from 'lucide-react';
import { APIKeyManager } from './APIKeyManager';
import { AIProvider } from '../types';

interface AIProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProvider: AIProvider;
  selectedModel: string;
  apiKeys: Record<AIProvider, string>;
  onProviderChange: (provider: AIProvider) => void;
  onModelChange: (model: string) => void;
  onApiKeyChange: (provider: AIProvider, key: string) => void;
  onValidateKey: (provider: AIProvider, apiKey: string) => Promise<boolean>;
  validationStatus: Record<AIProvider, 'idle' | 'validating' | 'valid' | 'invalid'>;
}

export const AIProviderModal: FC<AIProviderModalProps> = ({
  isOpen,
  onClose,
  selectedProvider,
  selectedModel,
  apiKeys,
  onProviderChange,
  onModelChange,
  onApiKeyChange,
  onValidateKey,
  validationStatus
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">AI Provider & Model Selection</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <APIKeyManager
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            apiKeys={apiKeys}
            onProviderChange={onProviderChange}
            onModelChange={onModelChange}
            onApiKeyChange={onApiKeyChange}
            onValidateKey={onValidateKey}
            validationStatus={validationStatus}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-between p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onClose}
            disabled={validationStatus[selectedProvider] !== 'valid' || !selectedModel}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {validationStatus[selectedProvider] === 'valid' && selectedModel
              ? `Continue with ${selectedProvider.toUpperCase()}`
              : 'Configure AI Provider First'
            }
          </button>
        </div>
      </div>
    </div>
  );
}
